import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, pairwise, startWith, takeUntil } from 'rxjs/operators';

import { ApModalService } from '@ap/modal';

import { Payload } from './channel/channel.model';
import { ApWebContainerChannelService } from './channel/channel.service';
import {
    AppType,
    DeviceIsNfcCapable,
    DeviceRegistrationToken,
    FlowType,
    IdType,
    NativeAppearanceSetting,
    NativeAppType,
    NativeCanNavigate,
    NativeHeaderState,
    NativeNavigation,
    NativeProcessStatus,
    NativeRequestMessage,
    NativeSelectedAccount,
    NativeSelectedDebitCard,
    NativeSelectedIdentification,
    NativeSign,
    ReadIdIdentificationResponse
} from './web-container.model';

/**
 * @ngdoc service
 * @name ApWebContainerService
 * @module ApWebContainerModule
 *
 * @description
 * This service can be used as a communication channel between a native app and
 * an angular web app running in a web container.
 */
@Injectable({ providedIn: 'root' })
export class ApWebContainerService implements OnDestroy {
    private readonly channelService = inject(ApWebContainerChannelService);
    private readonly modalService = inject(ApModalService);

    // Default state
    private currentNativeHeaderState: NativeHeaderState = {
        title: '',
        navigation: {
            back: false
        }
    };
    private savedNativeHeaderState: NativeHeaderState;
    private readonly navigateBack$: Subject<Payload>;
    private readonly destroy$: Subject<void>;
    private readonly headerStateHistory: NativeHeaderState[];

    public constructor() {
        this.destroy$ = new Subject<void>();
        this.navigateBack$ = new Subject<Payload>();
        this.headerStateHistory = [];
        this.setupModalListeners();
        this.setupContentSizeListener();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.navigateBack$.complete();
    }

    /**
     * @description
     * returns token used for push notifications
     *
     * @returns an observable containing the native application type
     */
    public getDeviceRegistrationToken(): Observable<DeviceRegistrationToken> {
        const id = this.channelService.post(NativeRequestMessage.GET_DEVICE_REGISTRATION_TOKEN);
        return this.channelService.getResponse<DeviceRegistrationToken>(id);
    }

    /**
     * @description
     * returns the native application type
     *
     * @returns an observable containing the native application type
     */
    public getAppType(): Observable<NativeAppType> {
        const id = this.channelService.post(NativeRequestMessage.GET_APP_TYPE);
        return this.channelService.getResponse<AppType>(id).pipe(map((response) => response.appType));
    }

    /**
     * @description
     * returns the appearance setting that is used to determine light or dark mode for the webcontainer
     *
     * @returns an observable containing the selected appearance setting
     */
    public getAppearanceSetting(): Observable<NativeAppearanceSetting> {
        const id = this.channelService.post(NativeRequestMessage.GET_APPEARANCE_SETTING);
        return this.channelService.getResponse<NativeAppearanceSetting>(id);
    }

    /**
     * @description
     * returns the account that was selected in the native app before entering the webcontainer
     *
     * @returns an observable containing the selected account
     */
    public getSelectedAccount(): Observable<NativeSelectedAccount> {
        const id = this.channelService.post(NativeRequestMessage.GET_SELECTED_ACCOUNT);
        return this.channelService.getResponse<NativeSelectedAccount>(id);
    }

    /**
     * @description
     * returns the debit card that was selected in the native app before entering the webcontainer
     *
     * @returns an observable containing the selected debit card
     */
    public getSelectedDebitCard(): Observable<NativeSelectedDebitCard> {
        const id = this.channelService.post(NativeRequestMessage.GET_SELECTED_DEBIT_CARD);
        return this.channelService.getResponse<NativeSelectedDebitCard>(id);
    }

    /**
     * @description
     * returns the identification that was selected in the nativate app before entering the webcontainer
     *
     * @returns an observable with one of the enumeration values
     */
    public getSelectedIdentification(): Observable<NativeSelectedIdentification> {
        const id = this.channelService.post(NativeRequestMessage.GET_SELECTED_IDENTIFICATION);
        return this.channelService.getResponse<NativeSelectedIdentification>(id);
    }

    /**
     * @description
     * requests the native app to completely handle the signing of the authenticationRequest
     *
     * @param actionId the actionId of the authenticationRequest
     * @param challenge a challenge needed when the native app is running on a different domain group as the angular web app
     * @returns an observable with the result from signing in the native app
     */
    public sign(actionId: string, challenge?: string): Observable<NativeSign> {
        const id = this.channelService.post(NativeRequestMessage.SIGN_NATIVE, { actionId, challenge });
        return this.channelService.getResponse<NativeSign>(id);
    }

    /**
     * @description
     * triggers the native app to display navigation buttons on top of the page
     *
     * @param navigation the controls to display
     */
    public setNavigation(navigation: NativeNavigation): void {
        this.currentNativeHeaderState = { ...this.currentNativeHeaderState, navigation };
        this.channelService.post(NativeRequestMessage.SET_NAVIGATION, navigation);
    }

    /**
     * @description
     * containing a boolean if the native app can navigate back. When this is false the back button should not be shown
     *
     * @returns an observable containing a boolean if back navigation is allowed
     */
    public canNavigate(): Observable<NativeCanNavigate> {
        const id = this.channelService.post(NativeRequestMessage.CAN_NAVIGATE);
        return this.channelService.getResponse<NativeCanNavigate>(id);
    }

    /**
     * @description
     * a request to check if the device that the app is running on has nfc capabilities which can be used for,
     * among others, scanning an id document
     *
     * @returns an observable containing a boolean if the current device is nfc capable
     */
    public isNfcCapable(): Observable<DeviceIsNfcCapable> {
        const id = this.channelService.post(NativeRequestMessage.GET_NFC_SUPPORT);
        return this.channelService.getResponse<DeviceIsNfcCapable>(id);
    }

    /**
     * @description
     * triggers the native app to start the ReadId identification process
     *
     * @param idType the type of id (passport or idCard) in the future, can be extended with different types of identification flows:
     * @param flowType the type of identification flow (nfcOnly, nfcFacialRecognition, ...)
     *
     * @param submitterIdentifier
     * @param attemptsRequiredToSkipNFC
     * @param shouldRequirePersonalNumber
     * @returns the response of the identification process, which contains a status and the ReadId sessionId
     */
    public startIdentification(
        idType: IdType,
        flowType: FlowType = FlowType.NFC_AND_FACIAL_RECOGNITION,
        submitterIdentifier: string = '',
        attemptsRequiredToSkipNFC: number = NaN,
        shouldRequirePersonalNumber: boolean = false
    ): Observable<ReadIdIdentificationResponse> {
        const id = this.channelService.post(NativeRequestMessage.START_IDENTIFICATION, {
            idType,
            flowType,
            submitterIdentifier,
            attemptsRequiredToSkipNFC,
            shouldRequirePersonalNumber
        });
        return this.channelService.getResponse<ReadIdIdentificationResponse>(id);
    }

    /**
     * @description
     * triggers the native app to close the webcontainer, start the native token registration process
     * and return to the returnUrl in a new webcontainer
     *
     * @param returnUrl is the url to visit in a new webcontainer after the native token is registered
     * @param isExistingCustomer denotes if the customer that triggered the process is an existing customer, or a new applicant
     * @param pairingCode can be used if the process has to be continued in a different way
     */
    public startTokenRegistration(returnUrl: string, isExistingCustomer: boolean, pairingCode: string): void {
        this.channelService.post(NativeRequestMessage.START_TOKEN_REGISTRATION, {
            returnUrl,
            isExistingCustomer,
            pairingCode
        });
    }

    /**
     * @description
     * triggers the native app to display a title in the native header
     * stores the value of title in the service state
     * @param title the title to display, leave empty for an empty native header
     */
    public setTitle(title: string): void {
        this.currentNativeHeaderState = { ...this.currentNativeHeaderState, title };
        this.channelService.post(NativeRequestMessage.SET_TITLE, { title });
    }

    /**
     * @description
     * communicates the end of the process to the native app
     *
     * @param processStatus the process end status
     */
    public finishProcess(processStatus: NativeProcessStatus): void {
        this.channelService.post(NativeRequestMessage.FINISH_PROCESS, { status: processStatus });
    }

    /**
     * @description
     * triggers the native app to end the current session and return to the login screen
     */
    public logOut(): void {
        this.channelService.post(NativeRequestMessage.LOG_OUT);
    }

    /**
     * @description
     * signal coming from the native app to navigate back in the process
     */
    public onNavigateBack(): Observable<Payload> {
        return this.navigateBack$.asObservable();
    }

    /**
     * @description
     * saves the current header state and disables the back navigation & removes the title
     */
    public hideNativeHeader(): void {
        if (this.savedNativeHeaderState) {
            return;
        }
        this.savedNativeHeaderState = this.currentNativeHeaderState;
        this.setNavigation({ back: false });
        this.setTitle('');
    }

    /**
     * @description
     * restores the native header state to the saved state
     */
    public showNativeHeader(): void {
        if (!this.savedNativeHeaderState) {
            return;
        }
        this.setNavigation(this.savedNativeHeaderState.navigation);
        this.setTitle(this.savedNativeHeaderState.title);
        delete this.savedNativeHeaderState;
    }

    /**
     * @description
     * communicates the active user to the native app
     */
    public setContentSize(size: string): void {
        this.channelService.post(NativeRequestMessage.SET_CONTENTSIZE, { size });
    }

    /**
     * @description
     * communicates the active user to the native app
     */
    public setUser(name: string, group?: string): void {
        this.channelService.post(NativeRequestMessage.SET_USER, { name, group });
    }

    /**
     * @description
     * communicates downloadable data to the native app
     */
    public downloadFile(base64Data: string, mediaType: string, filename: string): void {
        this.channelService.post(NativeRequestMessage.DOWNLOAD, {
            data: base64Data,
            type: mediaType,
            filename
        });
    }
    private setupListeners(type: NativeRequestMessage): Observable<Payload> {
        return this.channelService.receive().pipe(
            filter((payload) => payload.type === type),
            takeUntil(this.destroy$)
        );
    }

    private setupContentSizeListener() {
        this.setupListeners(NativeRequestMessage.SET_CONTENTSIZE).subscribe((payload) => {
            document.body.style.setProperty(payload.data.property ? payload.data.property : 'text-size-adjust', `${payload.data.size}`);
        });
    }
    /**
     * @description
     * Sets up listeners for modals so we can replace the modal header with the native header
     */
    private setupModalListeners(): void {
        // Here we listen for the NAVIGATE_BACK type. If there are open modals we close them.
        this.setupListeners(NativeRequestMessage.NAVIGATE_BACK).subscribe((payload) => {
            if (this.modalService.openModals > 0) {
                this.modalService.closeCurrentModal();
            } else {
                this.navigateBack$.next(payload);
            }
        });

        // Here we set the title to the modal when one opens.
        // We push the old header state on a stack so we can put it back when the modal closes.
        (this.modalService as any).modalCountChanged
            .pipe(startWith(0), pairwise(), takeUntil(this.destroy$))
            .subscribe(([previous, current]) => {
                if (current < previous) {
                    const previousHeaderState = this.headerStateHistory.pop();
                    this.setTitle(previousHeaderState.title);
                    this.setNavigation(previousHeaderState.navigation);
                } else {
                    this.headerStateHistory.push(this.currentNativeHeaderState);
                    this.setNavigation({ back: this.modalService.getCurrentModalIsCancelable() });
                    this.setTitle(this.modalService.getCurrentModalTitle());
                }
            });
    }
}
